/* eslint-disable no-restricted-imports */
import rawFoods from '../../../public/data/foods.json';

export interface FoodCategory {
    id: number;
    category_name: string;
    ficon: string;
}

interface FoodIngredient {
    name: string;
    is_meat?: boolean;
    is_halal?: boolean;
    is_allergen?: boolean;
    is_vegan?: boolean;
}
export interface FoodParameter {
    price: number;
    preparation_days: number;
    portion_quantity: number;
    daily_capacity: number;
    weight_percentage: number;
    is_top_seller: boolean;
    food_unit: string;
    portion_label: string;
}
export interface FoodItem {
    id: number;
    name: string;
    category: string;
    category_id?: number;
    description: string;
    ingredients: string[];
    image_path?: string;
    slug?: string;
    food_parameters: FoodParameter | null;
    is_available: boolean;
}

const categories: FoodCategory[] = rawFoods.categories;

const ingredients: FoodIngredient[] = rawFoods.ingredients;

const foods: FoodItem[] = rawFoods.foods as FoodItem[];

const categoryMap = new Map<string, FoodCategory>();
categories.forEach((cat) => categoryMap.set(cat.category_name, cat));

const getCategoryIdByName = (name: string): number => categoryMap.get(name)?.id ?? 0;

const getTopSellers = (): FoodItem[] => [];

export const getFoodBySlug = (slug: string): FoodItem | undefined =>
    foods.find(
        (f) =>
            f.name
                .toLowerCase()
                .replace(/[^a-z0-9\s-]/g, '')
                .replace(/\s+/g, '-')
                .replace(/-+/g, '-') === slug
    ) ?? foods.find((f) => String(f.id) === slug);

export const getAllergens = (food: FoodItem): string[] => {
    const allergenNames = ingredients.filter((ing) => ing.is_allergen).map((ing) => ing.name);
    return food.ingredients.filter((ing) => allergenNames.some((a) => a.toLowerCase() === ing.toLowerCase()));
};

const normalizeImagePath = (path?: string) => {
    if (!path) return '';
    if (path.startsWith('http') || path.startsWith('/') || path.startsWith('data:')) {
        return path;
    }
    return `/${path}`;
};

export const formatPortionLabel = (quantity?: number, unit?: string): string | undefined => {
    if (quantity === undefined || unit === undefined) return undefined;
    const needsSpace = !['g', 'ml'].includes(unit);
    return `${quantity}${needsSpace ? ' ' : ''}${unit}`;
};

export const toMenuItem = (food: FoodItem) => {
    const item = { ...food };
    const params = item.food_parameters;
    const normalizedImage = normalizeImagePath(item.image_path);
    return {
        id: item.id,
        name: item.name,
        item_name: item.name,
        category_id: getCategoryIdByName(item.category),
        category_name: item.category,
        description: item.description,
        item_image: normalizedImage,
        image_path: normalizedImage,
        ingredients: item.ingredients,
        allergens: getAllergens(item),
        price: params?.price ?? undefined,
        preparation_days: params?.preparation_days ?? undefined,
        portion_quantity: params?.portion_quantity ?? undefined,
        weight_percentage: params?.weight_percentage ?? undefined,
        is_top_seller: params?.is_top_seller ?? false,
        daily_capacity: params?.daily_capacity ?? undefined,
        food_unit: params?.food_unit ?? undefined,
        portion_label: params?.portion_label ?? undefined,
        is_available: item.is_available ?? true,
        food_parameters: params ?? null,
        slug: item.name
            .toLowerCase()
            .replace(/[^a-z0-9\s-]/g, '')
            .replace(/\s+/g, '-')
            .replace(/-+/g, '-'),
    };
};

const toCategoryItem = (cat: FoodCategory) => {
    const count = foods.filter((f) => getCategoryIdByName(f.category) === cat.id).length;
    return {
        id: cat.id,
        category_name: cat.category_name,
        name: cat.category_name,
        ficon: cat.ficon,
        icon: cat.ficon,
        count,
        slug: cat.category_name
            .toLowerCase()
            .replace(/[^a-z0-9\s-]/g, '')
            .replace(/\s+/g, '-')
            .replace(/-+/g, '-'),
    };
};

export const menuItems = foods.map(toMenuItem);
export const categoryItems = categories.map(toCategoryItem);
export const topSellerItems = getTopSellers().map(toMenuItem);

export const getMenuCategories = () =>
    categories.map((cat) => ({
        id: cat.id,
        name: cat.category_name,
        ficon: cat.ficon ?? null,
        isSelected: false,
    }));

export const getAvailableDate = (preparationDays?: number): Date | undefined => {
    if (preparationDays === undefined) return undefined;
    const date = new Date();
    date.setDate(date.getDate() + preparationDays);
    date.setHours(0, 0, 0, 0);
    return date;
};

export const formatAvailableDate = (
    date: Date,
    todayLabel: string,
    tomorrowLabel: string,
    fromLabel: string
): string => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (date.getTime() === today.getTime()) return todayLabel;
    if (date.getTime() === tomorrow.getTime()) return tomorrowLabel;

    return `${fromLabel} ${date.toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'long',
    })}`;
};

export const getUniqueAvailableDates = (): Date[] => {
    const dates = foods
        .map((f) => getAvailableDate(f.food_parameters?.preparation_days))
        .filter((d): d is Date => d !== undefined);
    const unique = new Map<string, Date>();
    dates.forEach((d) => {
        const key = d.toISOString().split('T')[0];
        if (!unique.has(key)) unique.set(key, d);
    });
    return Array.from(unique.values()).sort((a, b) => a.getTime() - b.getTime());
};

export const isFoodAvailableOnDate = (food: FoodItem, date: Date): boolean => {
    const availableDate = getAvailableDate(food.food_parameters?.preparation_days);
    if (!availableDate) return false;
    const target = new Date(date);
    target.setHours(0, 0, 0, 0);
    return availableDate.getTime() === target.getTime();
};
